import { h, FunctionComponent } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';
import * as styles from '~src/styles/domains/common/components/inputs.module.scss';
import { separateKebabCase } from '~src/utils/helpers';
import { trackMouseOut } from '~src/utils/tracking';
import { InfoIcon } from '../assets/svg';
import { Brand } from '../enums/common';
import {
  brandSelector,
  tooltipContainerRefSelector,
} from '../state/commonSelectors';

type Props = {
  id: string;
  trackingId?: string;
  classLabel?: string;
  showTooltip?: { (): void };
  hideTooltip?: { (): void };
};

const InfoTooltip: FunctionComponent<Props> = props => {
  const dispatch = useDispatch();
  const [isActive, setIsActive] = useState(false);
  const [enableStyle, setEnableStyle] = useState(false);
  const brand = useSelector(brandSelector);
  const isUplift = brand == Brand.UPLIFT;

  const { id, trackingId, classLabel, showTooltip, hideTooltip, children } =
    props;
  const node = useRef<HTMLElement>(null);

  const tooltipContainerRef = useSelector(tooltipContainerRefSelector);

  useEffect(() => {
    if (tooltipContainerRef && node?.current) setEnableStyle(true);
    else setEnableStyle(false);
  }, [tooltipContainerRef, node]);

  const setTooltipCss = () => {
    let style = { left: '', 'margin-left': '' };

    if (enableStyle) {
      const tooltipElement = node.current;
      const tooltipContainer = tooltipContainerRef;

      const containerBounds = tooltipContainer?.getBoundingClientRect();
      const containerRight = containerBounds?.right || 0;
      const containerLeft = containerBounds?.left || 0;

      // set display of tooltip before calculating
      if (tooltipElement) {
        tooltipElement.style.display = 'block';
      }

      // calculate after display is set
      const tooltipBounds = tooltipElement?.getBoundingClientRect();
      const tooltipRight = tooltipBounds?.right || 0;
      const tooltipLeft = tooltipBounds?.left || 0;
      const offsetLeft = containerLeft - tooltipLeft;
      const offsetRight = containerRight - tooltipRight;

      if (tooltipBounds && tooltipRight > containerRight) {
        style['margin-left'] = `${-110 + offsetRight}px`;
        return style;
      }

      if (tooltipBounds && tooltipLeft < containerLeft) {
        style['margin-left'] = `${-90 + offsetLeft}px`;
        return style;
      }
    }

    return {};
  };

  const setTooltipArrowCss = () => {
    let style = { left: '', 'margin-left': '' };
    if (enableStyle) {
      const tooltipElement = node.current;
      const tooltipContainer = tooltipContainerRef;

      const containerBounds = tooltipContainer?.getBoundingClientRect();
      const containerRight = containerBounds?.right || 0;
      const containerLeft = containerBounds?.left || 0;

      const tooltipBounds = tooltipElement?.getBoundingClientRect();
      const tooltipRight = tooltipBounds?.right;
      const tooltipLeft = tooltipBounds?.left;

      const offsetLeft = containerLeft - (tooltipLeft || 0);
      const offsetRight = tooltipRight || 0 - containerRight;

      if (tooltipBounds && (tooltipRight || 0) > containerRight) {
        style.left = `${110 + offsetRight}px`;
        style['margin-left'] = '-10px';
        return style;
      }

      if (tooltipBounds && (tooltipLeft || 0) < containerLeft) {
        style.left = `${80 - offsetLeft}px`;
        return style;
      }
    }
    return style;
  };

  const onClick = () => {
    setIsActive(true);
    if (showTooltip) showTooltip();
  };

  const onFocus = () => {
    setIsActive(true);
    if (showTooltip) showTooltip();
  };

  const onBlur = () => {
    setIsActive(false);
    if (hideTooltip) hideTooltip();
  };

  const onMouseEnter = () => {
    setIsActive(true);
    if (showTooltip) showTooltip();
  };

  const onMouseLeave = () => {
    setIsActive(false);
    dispatch(trackMouseOut(trackingId || id));
    if (hideTooltip) hideTooltip();
  };

  return (
    <div
      className={
        classLabel
          ? `${styles['up-info-tooltip']} ${classLabel}`
          : styles['up-info-tooltip']
      }
    >
      <button
        type='button'
        aria-describedby={id}
        aria-labelledby={id}
        aria-label='tooltip'
        aria-expanded={isActive}
        data-for={id}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <InfoIcon color={isUplift ? '#7C8594' : '#3a3b41'} />
      </button>
      <span
        ref={node}
        id={styles[id]}
        className={`${styles['up-info-tooltip-text']} 
        ${
          isActive
            ? styles['up-info-tooltip-visible']
            : styles['up-info-tooltip-hidden']
        }`}
        aria-hidden={isActive}
        role='tooltip'
        aria-label={separateKebabCase(id)}
        style={isActive ? setTooltipCss() : { display: '' }}
      >
        {children}
        <div
          className={styles['up-info-tooltip-text-dynamic-arrow']}
          tabIndex={-1}
          style={setTooltipArrowCss()}
        ></div>
      </span>
    </div>
  );
};

export default InfoTooltip;
